import type { AccountIdentifier, BbanOption, IbanOption } from '@api-client/accounts-webapi';
import type { AccountNo, AccountNoCZ } from '@shared/products-types';
import { formatBankCodeAndNumber } from '@shared/formatting-account-number-utils';
import { formatIBAN } from '@shared/formatting-utils';
import { getFeatureConfig } from '@luna/feature-config';

const RE_ACCOUNT_DISALLOWED_CHARS_HU = /[^0-9-]/gi;
const RE_TO_SPLIT_BY_CHUNKS_OF_8 = /.{1,8}/g;
const ACCOUNT_NO_PARTS_SEPARATOR = '-';
const BANK_CODE_LENGTH = 3;
const SPACED_BBAN_LAST_CHUNK_LENGTH = 2;

function unformatDBBAN(value: string): string {
    return value
        .replace(RE_ACCOUNT_DISALLOWED_CHARS_HU, '')
        .replace(new RegExp(ACCOUNT_NO_PARTS_SEPARATOR, 'g'), '');
}

function formatDBBAN(value: string): string {
    const rawValue = unformatDBBAN(value);
    const chunks = rawValue.match(RE_TO_SPLIT_BY_CHUNKS_OF_8) || [];

    return chunks.join(ACCOUNT_NO_PARTS_SEPARATOR);
}

function formatSPACEDBBAN(value: string): string {
    const bankCode = value.substring(0, BANK_CODE_LENGTH);
    const accountNumber = value.substring(
        BANK_CODE_LENGTH,
        value.length - SPACED_BBAN_LAST_CHUNK_LENGTH,
    );
    const lastChunk = value.substring(value.length - SPACED_BBAN_LAST_CHUNK_LENGTH);

    return `${bankCode} ${accountNumber} ${lastChunk}`;
}

export const formatAccountNumber = (accountno: AccountNo | undefined): string => {
    if (!accountno) {
        return '';
    }

    const preferredAccountNumberFormat = getFeatureConfig(
        (config) => config.accounts.preferredAccountNumberFormat,
    );

    if (preferredAccountNumberFormat === 'DBBAN' && accountno.number) {
        return formatDBBAN(accountno.number);
    }

    if (preferredAccountNumberFormat === 'SPACEDBBAN' && accountno.number) {
        return formatSPACEDBBAN(accountno.number);
    }

    if (preferredAccountNumberFormat === 'SBBAN' && accountno.number) {
        return formatBankCodeAndNumber(accountno.bankCode, accountno.number);
    }

    if (preferredAccountNumberFormat === 'IBAN' && !accountno.iban) {
        return formatBankCodeAndNumber(accountno.bankCode, accountno.number);
    }

    const shownIban = accountno.iban || (accountno as AccountNoCZ)['cz-iban'];

    // default IBAN
    return formatIBAN(shownIban);
};

export function formatAccountNo(accountno: AccountNo | undefined): string {
    if (!accountno) {
        return '';
    }

    if (accountno.iban) {
        return formatIBAN(accountno.iban);
    }

    return formatBankCodeAndNumber(accountno.bankCode, accountno.number);
}

export const formatAccountIdentifier = (accountIdentifier?: AccountIdentifier): string => {
    if (!accountIdentifier) {
        return '';
    }

    if ((accountIdentifier as IbanOption).iban) {
        return formatIBAN((accountIdentifier as IbanOption).iban);
    }

    const { bankCode, number } = accountIdentifier as BbanOption;
    return formatBankCodeAndNumber(bankCode, number);
};
